import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { Language, translations, Translations } from '../lib/translations';
import { getCurrentUser, updateUserMetadata, subscribeToAuthChanges } from '@/services/authService';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>('en');
  const [isLoading, setIsLoading] = useState(true);

  const applyLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, []);

  const loadLanguagePreference = useCallback(async () => {
    try {
      const userResult = await getCurrentUser();
      if (userResult.error) throw userResult.error;
      const user = userResult.data;
      if (user?.user_metadata?.language) {
        applyLanguage(user.user_metadata.language as Language);
      }
    } catch (error) {
      console.error('Error loading language preference:', error);
    } finally {
      setIsLoading(false);
    }
  }, [applyLanguage]);

  // Load language on mount and whenever auth state changes (e.g., switching accounts)
  useEffect(() => {
    loadLanguagePreference();
    const sub = subscribeToAuthChanges(() => {
      loadLanguagePreference();
    });
    return () => sub.unsubscribe();
  }, [loadLanguagePreference]);

  // Save language preference to Supabase and update state
  const setLanguage = async (lang: Language) => {
    applyLanguage(lang);

    try {
      const result = await updateUserMetadata({ language: lang });
      if (result.error) {
        console.error('Error saving language preference:', result.error);
      }
    } catch (error) {
      console.error('Error updating user metadata:', error);
    }

    // Update HTML dir attribute
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };

  // Apply dir attribute on initial load and language change
  useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
    isRTL: language === 'ar',
  };

  // Don't block rendering on loading - just use default language
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
